import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, FormInput, FormHelpText, FormPopoverTrigger, Popover } from '@spotify-internal/encore-web';

import { baseFieldFieldPropType } from 'utils/custom-proptypes';

import styles from './BaseField.module.scss';

class BaseField extends PureComponent {
  static propTypes = {
    field: baseFieldFieldPropType,
    t: PropTypes.func.isRequired,
    traw: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    value: PropTypes.string,
    valid: PropTypes.bool,
    forceValidation: PropTypes.bool,
    validateOnChange: PropTypes.bool,
    requireWithIsBlank: PropTypes.bool,
    tPrefix: PropTypes.string,
    errorMessageKey: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    usePopOver: PropTypes.bool,
    popOverMessage: PropTypes.string,
    // Because of the way it's done, I can't declare it in SelectField.js
    showRawOptionLabels: PropTypes.bool,
  };

  static defaultProps = {
    value: '',
    valid: false,
    forceValidation: false,
    validateOnChange: false,
    requireWithIsBlank: true,
    tPrefix: '',
    disabled: false,
    usePopOver: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      changed: false,
      popOverOpen: false,
    };

    this.updateFieldData();
    this.fieldChange = this.fieldChange.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { requireWithIsBlank, field } = this.props;

    if (prevProps.field.required !== field.required || prevProps.requireWithIsBlank !== requireWithIsBlank) {
      this.updateFieldData();
    }
  }

  updateFieldData() {
    const { field, value, onFieldChange } = this.props;
    const fieldData = {
      fieldIdentifier: field.name,
      value,
      valid: this.isValid(value),
    };
    onFieldChange(fieldData);
  }

  fieldChange({ target }) {
    const { field, onFieldChange } = this.props;
    const { changed } = this.state;

    if (!changed) {
      this.setState({ changed: true });
    }

    const valid = this.isValid(target.value);

    const fieldData = {
      fieldIdentifier: field.name,
      type: target.type,
      value: target.value,
      valid,
    };
    onFieldChange(fieldData);
  }

  isValid(value) {
    const { field, requireWithIsBlank } = this.props;

    if (field.required && requireWithIsBlank) {
      return Boolean(value.trim());
    }
    return true;
  }

  getLabel() {
    const { t, field, tPrefix } = this.props;

    if (field.labelOverride) {
      return t(tPrefix + field.labelOverride);
    }
    if (field.label) {
      return field.label;
    }
    return t(`${tPrefix}${field.name}_label`);
  }

  getPlaceholder() {
    const { t, field, tPrefix } = this.props;

    if (field.placeholderOverride) {
      return t(tPrefix + field.placeholderOverride);
    }

    return field.placeholder && t(`${tPrefix}${field.name}_placeholder`);
  }

  getIndicator() {
    const { field, requireWithIsBlank } = this.props;

    return field.required && requireWithIsBlank ? 'required' : null;
  }

  getHelpText() {
    const { traw, field, tPrefix } = this.props;

    if (!field.help_text) return null;

    return <FormHelpText className={styles.formHelpText}>{traw(`${tPrefix}${field.name}_help_text`)}</FormHelpText>;
  }

  showError() {
    const { forceValidation, validateOnChange, valid } = this.props;
    const { changed } = this.state;

    return (forceValidation || (validateOnChange && changed)) && !valid;
  }

  getError() {
    const { field } = this.props;
    const fieldErrorId = `${field.name}-error`;

    if (!this.showError()) return null;

    return (
      <FormHelpText id={fieldErrorId} data-testid="validation-error" error>
        {this.getErrorMessage()}
      </FormHelpText>
    );
  }

  getErrorMessage() {
    const { t, field, errorMessageKey } = this.props;

    if (field.required_with) {
      return t(errorMessageKey || 'contact_form:field_required_with');
    }

    // Checking if an object, which means it's a traw result, and we can render that directly
    if (typeof errorMessageKey === 'object' && !Array.isArray(errorMessageKey) && errorMessageKey !== null) {
      return errorMessageKey;
    }
    return t(errorMessageKey || 'contact_form:field_required');
  }

  getAriaDescribedBy() {
    const { field } = this.props;
    const fieldErrorId = `${field.name}-error`;

    return this.showError() ? fieldErrorId : field.name;
  }

  setPopover(value) {
    this.setState({
      popOverOpen: value,
    });
  }

  render() {
    const { field, value, className, disabled, usePopOver, popOverMessage, t } = this.props;
    const { popOverOpen } = this.state;

    return (
      <FormGroup
        data-error={this.showError()}
        key={field.name}
        label={this.getLabel()}
        labelFor={field.name}
        indicator={this.getIndicator()}
        popover={
          usePopOver && (
            <FormPopoverTrigger
              placement={FormPopoverTrigger.left}
              overlay={
                popOverOpen && (
                  <Popover arrow={Popover.left}>
                    {t(popOverMessage || 'infringement_claim_form:content_uris_tooltip')}
                  </Popover>
                )
              }
              onShow={() => this.setPopover(true)}
              onHide={() => this.setPopover(false)}
              aria-label="Help"
            />
          )
        }
      >
        <FormInput
          id={field.name}
          type={field.type}
          aria-describedby={this.getAriaDescribedBy()}
          aria-required={field.required}
          aria-invalid={this.showError()}
          placeholder={this.getPlaceholder()}
          onChange={this.fieldChange}
          value={value}
          error={this.showError()}
          maxLength={field.maxlength}
          className={className}
          autoComplete={field.autoComplete}
          disabled={disabled}
        />
        {this.getHelpText()}
        {this.getError()}
      </FormGroup>
    );
  }
}

export default BaseField;
